import { CheckedAddress, Percentage } from '@sparkdotfi/common-universal'
import { DeepPartial } from 'ts-essentials'
import { StateCreator } from 'zustand'

import { type StoreState } from '.'
import { tryOrDefault } from '@/utils/tryOrDefault'

export interface ActionsSettings {
  preferPermits: boolean
  setPreferPermits: (preferPermits: boolean) => void
  exchangeMaxSlippage: Percentage
  setExchangeMaxSlippage: (exchangeMaxSlippage: Percentage) => void
  txBatchingPreferenceByAddress: Record<CheckedAddress, boolean>
  setTxBatchingPreference: (address: CheckedAddress, preference: boolean) => void
  getTxBatchingPreference: (address: CheckedAddress) => boolean
}

export interface ActionsSettingsSlice {
  actionsSettings: ActionsSettings
}

export const defaultExchangeMaxSlippage = Percentage(0.0003)

// eslint-disable-next-line func-style
export const initActionsSettingsSlice: StateCreator<StoreState, [], [], ActionsSettingsSlice> = (set, get) => ({
  actionsSettings: {
    preferPermits: true,
    setPreferPermits: (preferPermits: boolean) =>
      set((state) => ({ actionsSettings: { ...state.actionsSettings, preferPermits } })),
    exchangeMaxSlippage: defaultExchangeMaxSlippage,
    setExchangeMaxSlippage: (exchangeMaxSlippage: Percentage) =>
      set((state) => ({ actionsSettings: { ...state.actionsSettings, exchangeMaxSlippage } })),
    txBatchingPreferenceByAddress: {},
    setTxBatchingPreference: (address: CheckedAddress, preference: boolean) =>
      set((state) => ({
        actionsSettings: {
          ...state.actionsSettings,
          txBatchingPreferenceByAddress: {
            ...state.actionsSettings.txBatchingPreferenceByAddress,
            [address]: preference,
          },
        },
      })),
    getTxBatchingPreference: (address: CheckedAddress) =>
      get().actionsSettings.txBatchingPreferenceByAddress[address] ?? true,
  },
})

export interface PersistedActionsSettingsSlice {
  actionsSettings: {
    preferPermits: boolean
    exchangeMaxSlippage: string
    txBatchingPreferenceByAddress: Record<string, boolean>
  }
}

export function persistActionsSettingsSlice(state: StoreState): PersistedActionsSettingsSlice {
  return {
    actionsSettings: {
      preferPermits: state.actionsSettings.preferPermits,
      exchangeMaxSlippage: state.actionsSettings.exchangeMaxSlippage.toFixed(),
      txBatchingPreferenceByAddress: state.actionsSettings.txBatchingPreferenceByAddress,
    },
  }
}

export function unPersistActionsSettingsSlice(
  persistedState: DeepPartial<PersistedActionsSettingsSlice>,
): DeepPartial<ActionsSettingsSlice> {
  if (!persistedState.actionsSettings) {
    return {}
  }

  return {
    actionsSettings: {
      preferPermits: persistedState.actionsSettings.preferPermits,
      exchangeMaxSlippage: tryOrDefault(() => {
        const rawSlippage = persistedState.actionsSettings?.exchangeMaxSlippage
        return rawSlippage && Percentage(rawSlippage)
      }, undefined),
      txBatchingPreferenceByAddress: persistedState.actionsSettings.txBatchingPreferenceByAddress ?? {},
    },
  }
}
